import * as ta from "./backend/alerts.generated.types";
import * as td from "./backend/datasources.generated.types";
import * as mc from "./../lib/ManufacturingConsts.generated";
import { INSIGHT_TYPES } from "./ManufacturingTags.generated";
import type { GanttChartSourceTypes, ManufacturingData } from "@/lib/dashboardTypes";

export type ITags = {
    [key: string]: string;
};

/** Timeseries datapoint returned by the dashboard engine. */
export type DashboardTimeseriesPoint = {
    ts: number;
    val: number;
    min?: number;
    max?: number;
};

/** Definition of a timeseries series in a dashboard data_def. */
export type DashboardDataDefTimeseries = {
    name: string;
    code?: string;
    uuid?: string;
    time_range?: string;
    aggr?: boolean;
};

/** Definition of an indicator/KPI in a dashboard data_def. */
export type DashboardDataDefIndicator = {
    key: string;
    type?: string;
    ts_code?: string | string[];
    ts_uuid?: string;
    warn?: string;
    error?: string;
    default_status?: string;
    operation?: string;
    material_uuid?: string;
    stock_locations?: string[];
    include_unconfirmed?: boolean;
    external_id?: string;
};

/**
 * External payload for manufacturing dashboard widgets.
 * Known keys are documented; additional keys are allowed (engine/custom widgets).
 */
export type DashboardExternalData = {
    manufacturing?: {
        line_uuids: string[];
        insight_types?: string[];
    };
    "manufacturing-gantt"?: {
        filters?: { line_groups?: string[]; lines?: string[] };
        show_only_tool_changes?: string;
        show_line_titles?: string;
        disable_zoom?: string;
    };
    "manufacturing-batch-operations"?: {
        batchop_group_uuid: string;
    };
    "shift-table"?: {
        filters?: {
            plant?: string;
            line_groups?: string[];
            lines?: string[];
            weeks?: number[];
        };
        weeks?: number[];
        line_group_uuid?: string;
        line_group_title?: string;
    };
    "manufacturing-tool-setup"?: {
        filters?: { plant_uuid?: string; line_group?: string };
    };
    refresh_time?: number;
    [key: string]: unknown;
};

/** Stored dashboard data definition (input to the dashboard engine). */
export type DashboardDataDef = {
    time_range?: string;
    timeseries?: DashboardDataDefTimeseries[];
    indicators?: DashboardDataDefIndicator[];
    alerts?: { tags?: ITags };
    events?: { datasource_codes?: string[]; tags?: ITags };
    external?: DashboardExternalData;
    parameters?: Record<string, string | number>;
};

export type RegisterUserObj = {
    title: string;
    username: string;
    password: string;
    password2: string;
    accept_terms: boolean;
    provider: string | null;
    provider_username: string;
};

export type LoginUserObj = {
    username: string;
    password: string;
    remember: boolean;
    otp: string;
};

export type LoggedinUserObj = {
    must_reset: boolean;
    title: string;
    username: string;
    uuid: string;
    home_dashboard: string | null;
    must_activate_twofa: boolean;
};

export type SelectedDatasourceObj = {
    datasource: td.IDatasourceExRec | null;
    analyses: td.IAnalysisModelEx[];
    recent_events_stats: ta.IGetStatsRes | null;
    events: ta.IEventData[];
};

// Manufacturing meta-data store

/**
 * Stock location object.
 */
export type StockLocationObj = {
    uuid: string;
    org_uuid: string;
    external_id: string;
    tags: ITags;
    title: string;
    plant_uuid: string;
};

/**
 * Material object.
 */
export type MaterialObj = {
    external_id: string;
    stock_locations?: StockLocationObj[];
    org_uuid: string;
    title: string;
    uuid: string;
};

export type ShiftBaseModel = {
    year: number;
    week: number;
    shift_number: number;
    shift_date: string;
};

/**
 * Shift number.
 */
export type ShiftNumber = {
    shift: number;
    day_number: number;
    week: number;
    year: number;
    day: string;
    shift_day: number;
};

/**
 * Shift data.
 */
export type ShiftNumberData = {
    day: string;
    day_number: number;
    shift: number;
    shift_day: number;
    shift_tag: string;
    ts_end: number;
    ts_start: number;
    week: number;
    year: number;
};

export type DashboardSaveObj = {
    active: boolean;
    data_def: DashboardDataDef;
    layout_def: DashboardLayout;
    public: boolean;
    tags: ITags;
    title: string;
};

export type DashboardBaseObj = {
    active: boolean;
    created_at: Date;
    created_by: string;
    is_system: boolean;
    org_uuid: string;
    public: boolean;
    tags: ITags;
    title: string;
    uuid: string;
};

export type DashboardObj = {
    active: boolean;
    created_at: Date;
    created_by: string;
    data_def: DashboardDataDef;
    is_system: boolean;
    layout_def: DashboardLayout;
    org_uuid: string;
    public: boolean;
    tags: ITags;
    title: string;
    uuid: string;
};

export type DashboardResultIndicatorObjTs = {
    key: string;
    type: string;
    val: number;
    status: string;
};

export type DashboardResultIndicatorObjLine = {
    key: string;
    type: string;
    title: string;
    status: string;
    product: string;
    person: string;
    parts_total: number;
    parts_produced: number;
    workorder_is_late: boolean;
    x: number;
    y: number;
    url: string;
};

export type DashboardResultIndicatorObj = DashboardResultIndicatorObjTs | DashboardResultIndicatorObjLine;

export type DashboardResultTimeseriesObj = {
    name: string;
    data: DashboardTimeseriesPoint[];
};

export type DashboardData = {
    from: number;
    to: number;
    alert_uuids: string[];
    event_uuids?: string[];
    indicators: DashboardResultIndicatorObj[];
    timeseries: DashboardResultTimeseriesObj[];
    events?: ta.IEventData[];
    external?: DashboardExternalData;
    source_type?: GanttChartSourceTypes;
    manufacturing?: ManufacturingData;
};

/**
 * Dashboard layout node (root, container, or leaf widget).
 * Fields are widget-type-specific; only `type` is always present on stored layouts.
 */
export type DashboardLayout = {
    type?: string;
    key?: string;
    value?: number;
    url?: string;
    timeseries?: string;
    height?: number;
    y_label?: string;
    y_max?: number;
    plot_type?: string;
    aggr?: string;
    uuid?: string;
    title?: string;
    compact?: boolean;
    label?: string;
    size?: string;
    show_title?: boolean;
    show_line_gantt?: boolean;
    bands?: BandType[];
    no_range_selector?: boolean;
    background?: string;
    indicators?: DashboardResultIndicatorObj[];
    indicator_type?: string;
    value_display_type?: string;
    dec_places?: number;
    unit?: string;
};

export type DashboardResultObj = {
    layout: DashboardLayout;
    data: DashboardData;
};

export type WeeklyReportWorker = {
    external_id: string;
    name: string;
};

export type WeeklyReportNorm = {
    capacity_factor: number;
    norm: number;
    norm_base_units: number;
    norm_break_time_hours: number;
    norm_machine_time: number;
};

export type WeeklyReport = {
    line_uuid: string;
    line_title: string;
    line_weight?: number;
    material_external_id: string;
    material_title: string;
    measurement_unit: string;
    norm: WeeklyReportNorm;
    order_external_id?: string;
    order_uuid?: string;
    sum: number;
    plan: number;
    shift_number: number;
    shift_time?: ShiftBaseModel;
    status?: number;
    worker: WeeklyReportWorker[];
};

export type RealizationShiftGrid = WeeklyReport[];
export type RealizationDayShiftGrid = RealizationShiftGrid[];
export type RealizationWeekShiftGrid = RealizationDayShiftGrid[];

/////////////////////////////////////////////////////////////////////

export const EVENT_TYPES = {
    man_downtime: "man.downtime",
};

export const INSIGHT_TYPES_CSS = {
    [INSIGHT_TYPES.freeze_order_move]: "plan",
    [INSIGHT_TYPES.man_downtime]: "insight-downtime",
    [INSIGHT_TYPES.no_person]: "people",
    [INSIGHT_TYPES.no_shift]: "people",
    [INSIGHT_TYPES.no_work_in_shift]: "work",
    [INSIGHT_TYPES.operation_delay_downstream]: "plan",
    [INSIGHT_TYPES.operation_no_input_material]: "plan",
    [INSIGHT_TYPES.operation_constraint_violated]: "plan",
    [INSIGHT_TYPES.out_of_stock_shopfloor]: "plan",
    [INSIGHT_TYPES.out_of_stock_plant]: "plan",
    [INSIGHT_TYPES.plan_change_end]: "plan",
    [INSIGHT_TYPES.plan_change_quantity]: "plan",
    [INSIGHT_TYPES.plan_change_shifts]: "plan",
    [INSIGHT_TYPES.plan_change_start]: "plan",
    [INSIGHT_TYPES.stock_requirement_delay]: "plan",
    [INSIGHT_TYPES.tool_setup]: "people",
};

export const INSIGHT_ACTIONS = {
    disable_shift: "disble_shift",
    enable_shift: "enable_shift",
    add_person: "add_person",
    remove_person: "remove_person",
    add_remove_person: "add_remove_person",
    increase_quantity: "increase_quantity",
    decrease_quantity: "decrease_quantity",
    change_work_order_dates: "change_work_order_dates",
    enable_24_7: "enable_24_7",
    change_work_order_sequence: "change_work_order_sequence",
    check_sap_for_shortage: "check_sap_for_shortage",
    replan_in_sap: "replan_in_sap",
    print_tool_transfer: "print_tool_transfer",
} as const;

export type InsightAction = (typeof INSIGHT_ACTIONS)[keyof typeof INSIGHT_ACTIONS];

// Defines for which insight types we want to show Actions button
export const INSIGHT_ACTIONS_ARRAY = [
    INSIGHT_TYPES.freeze_order_move,
    INSIGHT_TYPES.no_person,
    INSIGHT_TYPES.no_shift,
    INSIGHT_TYPES.out_of_stock_shopfloor,
    INSIGHT_TYPES.out_of_stock_plant,
    INSIGHT_TYPES.tool_setup,
];

export const JOB_TYPES = {
    electricity_consumption: "electricity_consumption",
    kpi_calculation: "kpi_calculation",
    manufacturing_checks: "manufacturing_checks",
    plan_import: "plan_import",
    plan_import_internal: "plan_import_internal",
    plan_table_export: "plan_table_export",
    production_planning: "production_planning",
    production_planning_wrapper: "production_planning_wrapper",
    production_simulation: "production_simulation",
    production_version_change: "production_version_change",
    rewire_parallel_materials: "rewire_parallel_materials",
    stock_requirements_simple: "stock_requirements_simple",
    update_unconfirmed_stock: "update_unconfirmed_stock",
};

export const KG_RELATIONS = {
    CUSTOMER_ORDERS_MATERIAL: "c-orders-m",
    LINEGROUP_CONSUMES_MATERIAL: "lg-consumes-m",
    LINEGROUP_PRODUCES_MATERIAL: "lg-produces-m",
    LINE_CONSUMES_MATERIAL: "l-consumes-m",
    LINE_PRODUCES_MATERIAL: "l-produces-m",
    MATERIAL_CONSUMES_MATERIAL: "m-consumes-m",
    PLANT_CONSUMES_MATERIAL: "p-consumes-m",
    PLANT_PRODUCES_MATERIAL: "p-produces-m",
    VENDOR_SUPPLIES_MATERIAL: "v-supplies-m",
};

export const BATCH_OPERATION_ITEM_STATUS = {
    CONFIRMED: "confirmed",
    FAILED: "failed",
    UNKNOWN: mc.UNKNOWN_CONST.unknown,
};

export enum SHIFT_MODIFIERS {
    none = "none",
    tpm = "TPM",
    value1 = "Red",
    value2 = "Blue",
}

export type UntilTodayOverall = {
    until_today: number;
    overall: number;
};

export type ProductionUntilTodayOverall = {
    produced: UntilTodayOverall;
    planned: UntilTodayOverall;
};

export type CalendarLineOption = {
    value: string;
    label: string;
};

/////////////////////////////////////////////////////////////////////

/** Layout band (panels) or bullet-graph plot band (from/to). */
export type BandType = {
    panels?: PanelType[];
    from?: number;
    to?: number;
    color?: string;
};

export type PanelType = {
    widgets?: DashboardLayout[];
    col_span?: number;
};

/**
 * Cleaning types
 *
 * These are hardcoded values for Agrana.
 */
export const CLEANING_TYPE = {
    unknown: "unknown",
    cip_kurz: "cip_kurz",
    cip_voll: "cip_voll",
    halal_cip: "halal_cip",
    kosher_cip: "kosher_cip",
    laugenkochung: "laugenkochung",
    pasteur_spulen_mit_kondensator: "pasteur_spulen_mit_kondensator",
    spuelen: "spuelen",
    uht_cip: "uht_cip",
    laugenkochung_cip_voll: "laugenkochung_cip_voll",
};

export const CLEANING_TYPE_LABEL = {
    [CLEANING_TYPE.unknown]: "unknown",
    [CLEANING_TYPE.cip_kurz]: "CIP kurz",
    [CLEANING_TYPE.cip_voll]: "CIP voll",
    [CLEANING_TYPE.halal_cip]: "Halal CIP",
    [CLEANING_TYPE.kosher_cip]: "Kosher CIP",
    [CLEANING_TYPE.laugenkochung]: "Laugenkochung",
    [CLEANING_TYPE.pasteur_spulen_mit_kondensator]: "Pasteur Spülen mit Kondensator",
    [CLEANING_TYPE.spuelen]: "Spülen",
    [CLEANING_TYPE.uht_cip]: "UHT CIP",
    [CLEANING_TYPE.laugenkochung_cip_voll]: "Laugenkochung + CIP voll",
};

/**
 * Cleaning durations
 *
 * These are hardcoded values for Agrana.
 */
export const CLEANING_DURATIONS = {
    [CLEANING_TYPE.unknown]: {},
    [CLEANING_TYPE.cip_kurz]: {
        P11: 72,
        P12: 77,
        P13: 127,
        P15: 120,
        P16: 120,
        P18: 60,
        P2: 107,
        P3: 110,
        P4: 60,
        P5: 107,
        P6: 107,
        P7: 107,
        P8: 120,
        S43: 120,
        S44: 120,
    },
    [CLEANING_TYPE.cip_voll]: {
        P11: 110,
        P12: 98,
        P13: 130,
        P15: 140,
        P16: 140,
        P2: 120,
        P3: 130,
        P4: 110,
        P5: 120,
        P6: 120,
        P7: 120,
        P8: 140,
        S43: 140,
        S44: 140,
    },
    [CLEANING_TYPE.halal_cip]: { P11: 15, P12: 15, P13: 15, P15: 15, P2: 15, P3: 10, P4: 15, P5: 15, P6: 15, P7: 15 },
    [CLEANING_TYPE.kosher_cip]: {
        BiB: 30,
        P11: 110,
        P12: 100,
        P13: 130,
        P15: 140,
        P16: 140,
        P2: 120,
        P3: 130,
        P4: 110,
        P5: 120,
        P6: 120,
        P7: 120,
        P8: 140,
        S43: 140,
        S44: 140,
    },
    [CLEANING_TYPE.laugenkochung]: {
        P11: 43,
        P12: 39,
        P13: 103,
        P3: 60,
        P4: 35,
    },
    [CLEANING_TYPE.pasteur_spulen_mit_kondensator]: {
        P11: 40,
        P12: 13,
        P4: 14,
    },
    [CLEANING_TYPE.spuelen]: {
        P11: 6,
        P12: 7,
        P13: 52,
        P15: 45,
        P16: 30,
        P18: 30,
        P2: 46,
        P3: 35,
        P4: 6,
        P5: 46,
        P6: 46,
        P7: 46,
        P8: 30,
        S43: 30,
        S44: 30,
    },
    [CLEANING_TYPE.uht_cip]: { P11: 150, P12: 128, P15: 160, P4: 119 },
    [CLEANING_TYPE.laugenkochung_cip_voll]: {},
};

/**
 * Cleaning rules
 *
 * These are hardcoded values for Agrana.
 */
export const CLEANING_RULE = {
    HerbAndSpices: "HerbAndSpices",
    CocoaMass: "CocoaMass",
    UHT: "UHT",
    Fig: "Fig",
    Fat: "Fat",
    FDA: "FDA",
    NutsOrGluten: "NutsOrGluten",
    OtherAllegens: "OtherAllegens",
    Kosher: "Kosher",
    Halal: "Halal",
    FlavorFree: "FlavorFree",
    OnlyNaturalFlavor: "OnlyNaturalFlavor",
    ConservedGood: "ConservedGood",
    GmoFree: "GmoFree",
    Bio: "Bio",
    Vegan: "Vegan",
    LactoseFree: "LactoseFree",
    ColorGradientWithPieces: "ColorGradientWithPieces",
    ColorGradientWithoutPieces: "ColorGradientWithoutPieces",
    Pieces: "Pieces",
    Vanilla: "Vanilla",
    FruitGroupChangeWithPieces: "FruitGroupChangeWithPieces",
    FruitGroupChangeWithoutPieces: "FruitGroupChangeWithoutPieces",
    DailyAtTetraI: "DailyAtTetraI",
    WeekendCleaningDisinfectionForTetra: "WeekendCleaningDisinfectionForTetra",
    AtLeastEvery44ProductionHours: "AtLeastEvery44ProductionHours",
    DailyAtTLine: "DailyAtTLine",
    Taste: "Taste",
};

export const CLEANING_RULE_LABEL = {
    [CLEANING_RULE.HerbAndSpices]: "Kräuter und Gewürze",
    [CLEANING_RULE.CocoaMass]: "Kakaomasse",
    [CLEANING_RULE.UHT]: "UHT",
    [CLEANING_RULE.Fig]: "Feige",
    [CLEANING_RULE.Fat]: "Fett",
    [CLEANING_RULE.FDA]: "FDA",
    [CLEANING_RULE.NutsOrGluten]: "Nüsse oder Gluten",
    [CLEANING_RULE.OtherAllegens]: "Andere Allergene",
    [CLEANING_RULE.Kosher]: "Koscher",
    [CLEANING_RULE.Halal]: "Halal",
    [CLEANING_RULE.FlavorFree]: "Geschmacksfrei",
    [CLEANING_RULE.OnlyNaturalFlavor]: "Nur natürliches Aroma",
    [CLEANING_RULE.ConservedGood]: "Konserviertes Gut",
    [CLEANING_RULE.GmoFree]: "Gentechnikfrei",
    [CLEANING_RULE.Bio]: "Bio",
    [CLEANING_RULE.Vegan]: "Vegan",
    [CLEANING_RULE.LactoseFree]: "Laktosefrei",
    [CLEANING_RULE.ColorGradientWithPieces]: "Farbverlauf mit Stücken",
    [CLEANING_RULE.ColorGradientWithoutPieces]: "Farbverlauf ohne Stücke",
    [CLEANING_RULE.Pieces]: "Stücke",
    [CLEANING_RULE.Vanilla]: "Vanille",
    [CLEANING_RULE.FruitGroupChangeWithPieces]: "Fruchtgruppenwechsel mit Stücken",
    [CLEANING_RULE.FruitGroupChangeWithoutPieces]: "Fruchtgruppenwechsel ohne Stücke",
    [CLEANING_RULE.DailyAtTetraI]: "Täglich bei TetraI",
    [CLEANING_RULE.WeekendCleaningDisinfectionForTetra]: "Wochenendreinigungen (Desinfektion bei Tetra)",
    [CLEANING_RULE.AtLeastEvery44ProductionHours]: "Spätestens alle 44 Produktionsstunden",
    [CLEANING_RULE.DailyAtTLine]: "Täglich bei T-Line",
    [CLEANING_RULE.Taste]: "Starker Geschmack",
};
